import { useMemo } from "react";
import { CheckCircle, XCircle, Eye, ChevronDown, ArrowRight } from "lucide-react";
import ChoixRenderer from "../../../VueQuestion/ChoixRenderer";
import {
    normalizeAnswer, parseComplexAnswer,
    compareAnswers, normalizeJsonForCompare, extractTrouWords, optionsDeQuestion, jugerTrous
} from "../../../../utils/questionUtils";
import { choixDeLaReponseImportee } from "../../../../utils/reponseImportee";

// ─── Utilitaires communs ───

// Transformer une réponse brute en choix pour ChoixRenderer
const parseChoix = (rep) => {
    if (rep.type === 'image') return { type: 'image', config: { imageUrl: rep.image } };
    if (rep.type === 'element' && typeof rep.contenu === 'string') {
        try { return JSON.parse(rep.contenu); } catch { return rep.contenu; }
    }
    return rep.contenu;
};

// Normaliser une réponse complexe [phrase|valeur] pour regroupement
const normalizeComplexAnswer = (answer) => {
    const parsed = parseComplexAnswer(answer);
    if (!parsed) return answer;
    const sorted = parsed
        .map(item => ({ phrase: normalizeAnswer(item.phrase), reponse: normalizeAnswer(item.reponse) }))
        .sort((a, b) => a.phrase.localeCompare(b.phrase));
    return sorted.map(item => `${item.phrase}|${item.reponse}`).join('||');
};

// Regrouper les élèves par réponse normalisée
const groupElevesByAnswer = (eleves, normalizeFn) => {
    const groups = new Map();
    eleves.forEach(eleve => {
        const key = normalizeFn(eleve.réponse || '');
        if (!groups.has(key)) {
            groups.set(key, { reponseOriginale: eleve.réponse, eleves: [] });
        }
        groups.get(key).eleves.push(eleve);
    });
    return Array.from(groups.values());
};

// ─── Composant wrapper pour la liste d'élèves extensible ───

const EleveExpandList = ({ students, isCorrect, isExpanded, expandKey, toggleExpand, handleEleveClick, bilan, onEleveClick, classeNom, totalStudents }) => {
    const count = students.length;
    const percent = totalStudents > 0 ? ((count / totalStudents) * 100).toFixed(0) : 0;

    return (
        <>
            {/* Stats + bouton expand */}
            <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                <span className={`text-sm xl:text-xs 2xl:text-xs 3xl:text-sm ${count > 0 ? 'text-gray-800' : 'text-gray-400'}`}>
                    {count} élève{count > 1 ? 's' : ''}
                </span>
                <span className={`text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-bold min-w-[40px] xl:min-w-[32px] text-right ${isCorrect ? 'text-emerald-600' : 'text-orange-500'}`}>
                    {percent}%
                </span>
                {count > 0 ? (
                    <button onClick={() => toggleExpand(expandKey)}
                        className={`p-1.5 xl:p-1 2xl:p-1 3xl:p-1.5 rounded-lg transition-all duration-200 flex items-center gap-1 ${
                            isExpanded ? 'bg-orange-500 text-white' : 'bg-blue-50 text-blue-500 hover:bg-blue-100 hover:text-blue-700'
                        }`}>
                        <Eye className="w-4 h-4 xl:w-3.5 xl:h-3.5 3xl:w-4 3xl:h-4" />
                        <ChevronDown className={`w-3 h-3 xl:w-2.5 xl:h-2.5 3xl:w-3 3xl:h-3 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                ) : (
                    <div className="w-[52px] xl:w-[42px]" />
                )}
            </div>
        </>
    );
};

// Panneau extensible avec la liste des élèves
const ElevePanel = ({ students, isCorrect, isExpanded, handleEleveClick, bilan, onEleveClick, classeNom }) => (
    <div className={`overflow-hidden transition-all duration-300 ease-out ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className={`px-4 xl:px-3 py-3 xl:py-2 border border-t-0 rounded-b-xl ${
            isCorrect ? 'bg-emerald-50/50 border-emerald-200' : 'bg-orange-50/50 border-orange-200'
        }`}>
            <p className="text-[10px] text-gray-400 italic mb-2">Cliquez sur un élève pour voir sa réussite à l'évaluation</p>
            <div className="flex flex-wrap gap-2 xl:gap-1.5">
                {students.map((eleve, i) => (
                    <div
                        key={i}
                        onClick={() => handleEleveClick(eleve.nom, eleve.classe)}
                        className={`flex items-center gap-2 xl:gap-1.5 px-3 xl:px-2 py-1.5 xl:py-1 rounded-full text-sm xl:text-xs bg-white ${
                            isCorrect ? 'text-emerald-800 border-2 border-emerald-200' : 'text-orange-800 border-2 border-red-200'
                        } ${(!bilan || onEleveClick) ? 'cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200' : ''}`}
                    >
                        <div className={`w-6 h-6 xl:w-5 xl:h-5 rounded-full flex items-center justify-center text-xs xl:text-[10px] font-bold text-white ${
                            isCorrect ? 'bg-emerald-500' : 'bg-orange-500'
                        }`}>
                            {eleve.nom.charAt(0)}
                        </div>
                        {!classeNom && (
                            <span className="text-xs xl:text-[10px] font-medium opacity-70 pr-1 border-r border-gray-300">{eleve.classe}</span>
                        )}
                        {eleve.nom}
                    </div>
                ))}
            </div>
        </div>
    </div>
);

// Wrapper pour une ligne de distribution (barre de progression + contenu)
const DistributionRow = ({ isCorrect, percent, isExpanded, expandKey, children, students, toggleExpand, handleEleveClick, bilan, onEleveClick, classeNom, totalStudents }) => (
    <div className="overflow-hidden rounded-xl shadow-sm">
        <div className={`relative overflow-hidden p-3 xl:p-2 2xl:p-2.5 3xl:p-3 transition-all duration-300 bg-white border ${
            isCorrect ? 'border-emerald-300' : 'border-slate-200'
        } ${isExpanded ? 'rounded-t-xl rounded-b-none border-b-0' : 'rounded-xl'}`}>
            <div className="absolute inset-0 bg-slate-50/80" style={{ width: '100%' }} />
            <div className={`absolute inset-0 ${isCorrect ? 'bg-gradient-to-r from-emerald-400/40 via-emerald-300/50 to-teal-300/40' : 'bg-gradient-to-r from-orange-400/40 via-amber-300/50 to-orange-300/40'} transition-all duration-500`}
                style={{ width: `${percent}%` }} />
            <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3 xl:gap-2 flex-1 min-w-0">
                    {isCorrect
                        ? <CheckCircle className="w-5 h-5 xl:w-4 xl:h-4 text-emerald-500 flex-shrink-0" />
                        : <XCircle className="w-5 h-5 xl:w-4 xl:h-4 text-orange-400 flex-shrink-0" />
                    }
                    <div className="flex-1 min-w-0">{children}</div>
                </div>
                <EleveExpandList
                    students={students}
                    isCorrect={isCorrect}
                    isExpanded={isExpanded}
                    expandKey={expandKey}
                    toggleExpand={toggleExpand}
                    handleEleveClick={handleEleveClick}
                    bilan={bilan}
                    onEleveClick={onEleveClick}
                    classeNom={classeNom}
                    totalStudents={totalStudents}
                />
            </div>
        </div>
        <ElevePanel
            students={students}
            isCorrect={isCorrect}
            isExpanded={isExpanded}
            handleEleveClick={handleEleveClick}
            bilan={bilan}
            onEleveClick={onEleveClick}
            classeNom={classeNom}
        />
    </div>
);

// ═══════════════════════════════════════════════════════════
// ─── Distribution QCM (choix prédéfinis) ───
// ═══════════════════════════════════════════════════════════

export const DistributionQCM = ({ index, itemDetail, expandedAnswers, toggleExpand, totalStudents, handleEleveClick, bilan, onEleveClick, classeNom }) => {
    const allEleves = useMemo(() => [...itemDetail.reussites, ...itemDetail.echecs], [itemDetail]);
    // Le choix de chaque élève, une seule fois : une réponse importée retrouve son choix même quand l'export du
    // ministère l'a abrégée ou a perdu un exposant (utils/reponseImportee.js).
    const choixDesEleves = useMemo(
        () => allEleves.map((eleve) => choixDeLaReponseImportee(eleve.réponse, index.reponses)),
        [allEleves, index.reponses]
    );
    // Les élèves sans réponse ont leur ligne : le total de la répartition fait le nombre d'élèves.
    const sansReponse = allEleves.filter((eleve) => !String(eleve.réponse ?? '').trim());
    const cleSansReponse = `simple-${index.reponses.length}`;

    const lignes = index.reponses.map((r, key) => {
        const rValue = r.type === 'image' ? r.image : r.contenu;
        const students = allEleves.filter((_, i) => choixDesEleves[i] === key);
        const percent = totalStudents > 0 ? ((students.length / totalStudents) * 100).toFixed(0) : 0;
        const isCorrect = normalizeAnswer(rValue) === normalizeAnswer(index.bonne_reponse)
            || (!!index.bonne_reponse_image && normalizeAnswer(rValue) === normalizeAnswer(index.bonne_reponse_image))
            || (r.type === 'image' && r.contenu && normalizeAnswer(r.contenu) === normalizeAnswer(index.bonne_reponse));
        const expandKey = `simple-${key}`;

        return (
            <DistributionRow
                key={key} isCorrect={isCorrect} percent={percent}
                isExpanded={expandedAnswers[expandKey]} expandKey={expandKey}
                students={students} toggleExpand={toggleExpand}
                handleEleveClick={handleEleveClick} bilan={bilan} onEleveClick={onEleveClick}
                classeNom={classeNom} totalStudents={totalStudents}
            >
                <div className="flex items-center gap-2">
                    {/* La police des choix des exerciseurs (Nunito, font-sans), pas celle de l'interface ; un choix
                        image en mode compact, comme dans les résultats des exerciseurs (DistributionExerciseur) :
                        taille naturelle bornée (300 px de large, 150 de haut), agrandie au clic. */}
                    <ChoixRenderer
                        choix={parseChoix(r)}
                        light
                        compact
                        className={`font-sans font-semibold text-sm xl:text-xs ${isCorrect ? 'text-emerald-700' : 'text-orange-700'}`}
                    />
                    {isCorrect && (
                        <span className="text-xs xl:text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium border border-emerald-200">
                            Bonne réponse
                        </span>
                    )}
                </div>
            </DistributionRow>
        );
    });

    return (
        <>
            {lignes}
            {sansReponse.length > 0 ? (
                <DistributionRow
                    isCorrect={false}
                    percent={totalStudents > 0 ? ((sansReponse.length / totalStudents) * 100).toFixed(0) : 0}
                    isExpanded={expandedAnswers[cleSansReponse]} expandKey={cleSansReponse}
                    students={sansReponse} toggleExpand={toggleExpand}
                    handleEleveClick={handleEleveClick} bilan={bilan} onEleveClick={onEleveClick}
                    classeNom={classeNom} totalStudents={totalStudents}
                >
                    <span className="font-sans font-semibold text-sm xl:text-xs text-gray-500 italic">Sans réponse</span>
                </DistributionRow>
            ) : null}
        </>
    );
};

// ═══════════════════════════════════════════════════════════
// ─── Distribution Complex ([phrase|valeur]) ───
// ═══════════════════════════════════════════════════════════

export const DistributionComplex = ({ index, itemDetail, expandedAnswers, toggleExpand, totalStudents, handleEleveClick, bilan, onEleveClick, classeNom }) => {
    const groups = useMemo(() => {
        const allEleves = [...itemDetail.reussites, ...itemDetail.echecs];
        return groupElevesByAnswer(allEleves, normalizeComplexAnswer);
    }, [itemDetail]);

    const bonnesReponses = useMemo(() => parseComplexAnswer(index.bonne_reponse), [index.bonne_reponse]);

    return groups.map((group, key) => {
        const eleveReponses = parseComplexAnswer(group.reponseOriginale);
        let correctSubAnswers = 0;
        eleveReponses?.forEach(eleveRep => {
            const bonneRep = bonnesReponses?.find(br => normalizeAnswer(br.phrase) === normalizeAnswer(eleveRep.phrase));
            if (bonneRep && normalizeAnswer(bonneRep.reponse) === normalizeAnswer(eleveRep.reponse)) correctSubAnswers++;
        });
        const totalSubAnswers = bonnesReponses?.length || 4;
        const isFullyCorrect = correctSubAnswers === totalSubAnswers;
        const expandKey = `complex-${key}`;
        const percent = totalStudents > 0 ? ((group.eleves.length / totalStudents) * 100).toFixed(0) : 0;

        return (
            <DistributionRow
                key={key} isCorrect={isFullyCorrect} percent={percent}
                isExpanded={expandedAnswers[expandKey]} expandKey={expandKey}
                students={group.eleves} toggleExpand={toggleExpand}
                handleEleveClick={handleEleveClick} bilan={bilan} onEleveClick={onEleveClick}
                classeNom={classeNom} totalStudents={totalStudents}
            >
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        {isFullyCorrect
                            ? <span className="text-sm xl:text-xs font-medium text-emerald-700">Tout correct</span>
                            : <span className="text-xs font-bold px-2 py-0.5 rounded bg-orange-100 text-orange-700">{correctSubAnswers}/{totalSubAnswers}</span>
                        }
                    </div>
                    <div className="space-y-0.5">
                        {eleveReponses?.map((item, idx) => {
                            const bonneRep = bonnesReponses?.find(br => normalizeAnswer(br.phrase) === normalizeAnswer(item.phrase));
                            const isSubCorrect = bonneRep && normalizeAnswer(bonneRep.reponse) === normalizeAnswer(item.reponse);
                            return (
                                <div key={idx} className={`flex items-start gap-1.5 text-xs ${isSubCorrect ? 'text-emerald-700' : 'text-orange-600'}`}>
                                    <span className="font-bold">{isSubCorrect ? '✓' : '✗'}</span>
                                    <span><span className="font-medium">{item.phrase}</span> : <span className="font-semibold">{item.reponse}</span></span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </DistributionRow>
        );
    });
};

// ═══════════════════════════════════════════════════════════
// ─── Distribution Texte libre (regroupement par réponse) ───
// ═══════════════════════════════════════════════════════════

export const DistributionTexteLibre = ({ index, itemDetail, expandedAnswers, toggleExpand, totalStudents, handleEleveClick, bilan, onEleveClick, classeNom }) => {
    const groups = useMemo(() => {
        const allEleves = [...itemDetail.reussites, ...itemDetail.echecs];
        return groupElevesByAnswer(allEleves, normalizeAnswer);
    }, [itemDetail]);

    return groups.map((group, key) => {
        const isCorrect = compareAnswers(group.reponseOriginale, index.bonne_reponse, index.bonne_reponse_image, optionsDeQuestion(index));
        const percent = totalStudents > 0 ? ((group.eleves.length / totalStudents) * 100).toFixed(0) : 0;
        const expandKey = `texte-${key}`;

        return (
            <DistributionRow
                key={key} isCorrect={isCorrect} percent={percent}
                isExpanded={expandedAnswers[expandKey]} expandKey={expandKey}
                students={group.eleves} toggleExpand={toggleExpand}
                handleEleveClick={handleEleveClick} bilan={bilan} onEleveClick={onEleveClick}
                classeNom={classeNom} totalStudents={totalStudents}
            >
                <div className="flex items-center gap-2">
                    <span className={`font-medium text-sm xl:text-xs ${isCorrect ? 'text-emerald-700' : 'text-orange-700'}`}>
                        {group.reponseOriginale || <span className="italic text-gray-400">Pas de réponse</span>}
                    </span>
                    {isCorrect && (
                        <span className="text-xs xl:text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium border border-emerald-200">
                            Bonne réponse
                        </span>
                    )}
                </div>
            </DistributionRow>
        );
    });
};

// ═══════════════════════════════════════════════════════════
// ─── Distribution Relier (paires source→cible) ───
// ═══════════════════════════════════════════════════════════

export const DistributionRelier = ({ index, itemDetail, expandedAnswers, toggleExpand, totalStudents, handleEleveClick, bilan, onEleveClick, classeNom }) => {
    const groups = useMemo(() => {
        const allEleves = [...itemDetail.reussites, ...itemDetail.echecs];
        return groupElevesByAnswer(allEleves, normalizeJsonForCompare);
    }, [itemDetail]);

    const correctPairs = useMemo(() => {
        try { return new Map(JSON.parse(index.bonne_reponse).map(([s, t]) => [s, t])); }
        catch { return new Map(); }
    }, [index.bonne_reponse]);

    return groups.map((group, key) => {
        const isCorrect = compareAnswers(group.reponseOriginale, index.bonne_reponse, null, optionsDeQuestion(index));
        const percent = totalStudents > 0 ? ((group.eleves.length / totalStudents) * 100).toFixed(0) : 0;
        const expandKey = `relier-${key}`;
        let pairs = [];
        try { pairs = JSON.parse(group.reponseOriginale); } catch { /* ignore */ }

        return (
            <DistributionRow
                key={key} isCorrect={isCorrect} percent={percent}
                isExpanded={expandedAnswers[expandKey]} expandKey={expandKey}
                students={group.eleves} toggleExpand={toggleExpand}
                handleEleveClick={handleEleveClick} bilan={bilan} onEleveClick={onEleveClick}
                classeNom={classeNom} totalStudents={totalStudents}
            >
                <div className="space-y-0.5">
                    {pairs.map(([source, target], i) => {
                        const pairCorrect = correctPairs.get(source) === target;
                        return (
                            <div key={i} className={`flex items-center gap-1.5 text-xs ${pairCorrect ? 'text-emerald-700' : 'text-orange-600'}`}>
                                <span className="font-bold">{pairCorrect ? '✓' : '✗'}</span>
                                <span className="font-medium">{source}</span>
                                <ArrowRight size={10} className="text-gray-400" />
                                <span className="font-semibold">{target}</span>
                            </div>
                        );
                    })}
                </div>
            </DistributionRow>
        );
    });
};

// ═══════════════════════════════════════════════════════════
// ─── Distribution Ordonner (liste ordonnée) ───
// ═══════════════════════════════════════════════════════════

export const DistributionOrdonner = ({ index, itemDetail, expandedAnswers, toggleExpand, totalStudents, handleEleveClick, bilan, onEleveClick, classeNom }) => {
    const groups = useMemo(() => {
        const allEleves = [...itemDetail.reussites, ...itemDetail.echecs];
        return groupElevesByAnswer(allEleves, normalizeJsonForCompare);
    }, [itemDetail]);

    const correctItems = useMemo(() => {
        try { return JSON.parse(index.bonne_reponse); } catch { return []; }
    }, [index.bonne_reponse]);

    return groups.map((group, key) => {
        const isCorrect = compareAnswers(group.reponseOriginale, index.bonne_reponse, null, optionsDeQuestion(index));
        const percent = totalStudents > 0 ? ((group.eleves.length / totalStudents) * 100).toFixed(0) : 0;
        const expandKey = `ordonner-${key}`;
        let items = [];
        try { items = JSON.parse(group.reponseOriginale); } catch { /* ignore */ }

        return (
            <DistributionRow
                key={key} isCorrect={isCorrect} percent={percent}
                isExpanded={expandedAnswers[expandKey]} expandKey={expandKey}
                students={group.eleves} toggleExpand={toggleExpand}
                handleEleveClick={handleEleveClick} bilan={bilan} onEleveClick={onEleveClick}
                classeNom={classeNom} totalStudents={totalStudents}
            >
                <div className="flex flex-wrap gap-1">
                    {items.map((item, i) => {
                        const posCorrect = item === correctItems[i];
                        return (
                            <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                                posCorrect ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'
                            }`}>
                                <span className="font-bold">{i + 1}.</span> {item}
                            </span>
                        );
                    })}
                </div>
            </DistributionRow>
        );
    });
};

// ═══════════════════════════════════════════════════════════
// ─── Distribution Texte à trous (phrase avec mots) ───
// ═══════════════════════════════════════════════════════════

export const DistributionTexteTrou = ({ index, itemDetail, expandedAnswers, toggleExpand, totalStudents, handleEleveClick, bilan, onEleveClick, classeNom }) => {
    const groups = useMemo(() => {
        const allEleves = [...itemDetail.reussites, ...itemDetail.echecs];
        // Regrouper par les mots dans les trous (normalisés)
        return groupElevesByAnswer(allEleves, (rep) => extractTrouWords(rep || '').join('|'));
    }, [itemDetail]);

    const correctWords = useMemo(() => extractTrouWords(index.bonne_reponse || ''), [index.bonne_reponse]);

    return groups.map((group, key) => {
        const options = optionsDeQuestion(index);
        const isCorrect = compareAnswers(group.reponseOriginale, index.bonne_reponse, null, options);
        const percent = totalStudents > 0 ? ((group.eleves.length / totalStudents) * 100).toFixed(0) : 0;
        const expandKey = `trou-${key}`;
        const eleveWords = extractTrouWords(group.reponseOriginale || '');
        // Chaque mot suit la règle du score (tolérance, trous sans ordre : 2026-09-29).
        const verdicts = jugerTrous(eleveWords, correctWords, options);

        return (
            <DistributionRow
                key={key} isCorrect={isCorrect} percent={percent}
                isExpanded={expandedAnswers[expandKey]} expandKey={expandKey}
                students={group.eleves} toggleExpand={toggleExpand}
                handleEleveClick={handleEleveClick} bilan={bilan} onEleveClick={onEleveClick}
                classeNom={classeNom} totalStudents={totalStudents}
            >
                <div className="flex flex-wrap gap-1">
                    {eleveWords.map((word, i) => {
                        const wordCorrect = verdicts[i]?.juste === true;
                        return (
                            <span key={i} className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${
                                wordCorrect ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-orange-50 text-orange-700 border-orange-200'
                            }`}>
                                {word}
                            </span>
                        );
                    })}
                </div>
            </DistributionRow>
        );
    });
};

// ═══════════════════════════════════════════════════════════
// ─── Distribution Classer (catégories) ───
// ═══════════════════════════════════════════════════════════

export const DistributionClasser = ({ index, itemDetail, expandedAnswers, toggleExpand, totalStudents, handleEleveClick, bilan, onEleveClick, classeNom }) => {
    const groups = useMemo(() => {
        const allEleves = [...itemDetail.reussites, ...itemDetail.echecs];
        return groupElevesByAnswer(allEleves, normalizeJsonForCompare);
    }, [itemDetail]);

    // Map inversée : item → catégorie correcte
    const correctCatMap = useMemo(() => {
        try {
            const data = JSON.parse(index.bonne_reponse);
            const map = {};
            Object.entries(data).forEach(([cat, items]) => {
                (items || []).forEach(item => { map[item] = cat; });
            });
            return map;
        } catch { return {}; }
    }, [index.bonne_reponse]);

    return groups.map((group, key) => {
        const isCorrect = compareAnswers(group.reponseOriginale, index.bonne_reponse, null, optionsDeQuestion(index));
        const percent = totalStudents > 0 ? ((group.eleves.length / totalStudents) * 100).toFixed(0) : 0;
        const expandKey = `classer-${key}`;
        let eleveData = {};
        try { eleveData = JSON.parse(group.reponseOriginale); } catch { /* ignore */ }

        return (
            <DistributionRow
                key={key} isCorrect={isCorrect} percent={percent}
                isExpanded={expandedAnswers[expandKey]} expandKey={expandKey}
                students={group.eleves} toggleExpand={toggleExpand}
                handleEleveClick={handleEleveClick} bilan={bilan} onEleveClick={onEleveClick}
                classeNom={classeNom} totalStudents={totalStudents}
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 w-full">
                    {Object.entries(eleveData).map(([catName, items], ci) => (
                        <div key={ci} className="flex items-start gap-1.5 px-2 py-1.5 rounded-lg bg-slate-100 border border-slate-200 shadow-sm">
                            <span className="text-[10px] font-bold text-gray-500 whitespace-nowrap mt-0.5">{catName} :</span>
                            <div className="flex flex-wrap gap-0.5">
                                {(items || []).map((item, ii) => {
                                    const itemCorrect = correctCatMap[item] === catName;
                                    return (
                                        <span key={ii} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                            itemCorrect ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'
                                        }`}>
                                            {item}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </DistributionRow>
        );
    });
};
